import React, { useCallback, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as LOCALSTORAGE_KEYS } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { learnMoreURL } from '../constants.ts';
import { useIsAutoConvertedRecently } from '../hooks/useIsAutoConvertedRecently/useIsAutoConvertedRecently.ts';
import { useMarkAutoConvertedDate } from '../hooks/useMarkAutoConvertedDate/useMarkAutoConvertedDate.ts';

export const i18n = defineMessages({
	pageHasBeenConvertedTitle: {
		id: 'editor-features.page-has-been-converted-title',
		description:
			'Page Opt In Migration Flag title to show that page has been recently converted to Fabric',
		defaultMessage: 'Page has been converted',
	},
	pageHasBeenConvertedDescription: {
		id: 'editor-features.page-has-been-converted-description',
		description:
			'Page Opt In Migration Flag description to show that page has been recently converted to Fabric',
		defaultMessage: 'This page has been converted to the new editing experience',
	},
	pageHasBeenConvertedLearnMore: {
		id: 'editor-features.page-has-been-converted-learn-more-link',
		description:
			'Page Opt In Migration Flag call to action to direct user to url where they learn more about pages converted to new fabric editor',
		defaultMessage: 'Learn more',
	},
});

export type MigrationSuccessFlagComponentProps = {
	contentId: string;
	flags: FlagsStateContainer;
};

type FlagComponentProps = Omit<MigrationSuccessFlagComponentProps, 'contentId'> & {
	onShowFlag: () => void;
};

export const onClickLearnMore = () => {
	window.open(learnMoreURL, '_blank');
};

const FlagComponent = React.memo(({ flags, onShowFlag }: FlagComponentProps) => {
	const showFlag = useRef<boolean>(true);

	// Show the flag and trigger any side effects once
	if (showFlag) {
		void flags.showInfoFlag({
			title: <FormattedMessage {...i18n.pageHasBeenConvertedTitle} />,
			description: <FormattedMessage {...i18n.pageHasBeenConvertedDescription} />,
			actions: [
				{
					content: <FormattedMessage {...i18n.pageHasBeenConvertedLearnMore} />,
					onClick: onClickLearnMore,
				},
			],
			// Auto-dismiss the flag after 8 seconds (AtlasKit AutoDismissFlag default dismiss delay)
			isAutoDismiss: true,
		});
		onShowFlag();
		showFlag.current = false;
	}

	return null;
});

export const MigrationSuccessFlagComponentV2 = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXPERIENCES,
})(({ contentId, flags }: MigrationSuccessFlagComponentProps) => {
	const { cloudId } = useSessionData();

	// Get relevant auto-conversion data
	const { loading, error, isAutoConverted, isMarkedAutoConverted } = useIsAutoConvertedRecently({
		contentId,
	});

	// Determine if current user is the first one to interact with an auto-converted legacy page
	const didAutoConversionRecently = !loading && !error && isAutoConverted && !isMarkedAutoConverted;
	// This key is set previously when a user manually converts a page (i.e. clicks "Convert page")
	const didManualConversionRecently = useMemo(
		() => localStorage.getItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED) === contentId,
		[contentId],
	);

	// Mutation to mark auto-converted page with autoConvertedDate content property
	const { markAutoConvertedDate } = useMarkAutoConvertedDate({
		cloudId,
		contentId,
	});

	const handleShowFlagManualConversion = useCallback(() => {
		localStorage.removeItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED);
	}, []);

	const handleShowFlagAutoConversion = useCallback(() => {
		markAutoConvertedDate();
	}, [markAutoConvertedDate]);

	if (didManualConversionRecently) {
		// In the case of a recent manual conversion, we want to:
		// (1) Show the flag.
		// (2) Clear local storage since the manual conversion has completed and the user has received visual confirmation.
		return <FlagComponent flags={flags} onShowFlag={handleShowFlagManualConversion} />;
	} else if (didAutoConversionRecently) {
		// In the case of being the first visitor of an auto-converted page, we want to:
		// (1) Show the flag.
		// (2) Mark the page with an auto-conversion timestamp indicating this page has been visited. Subsequent page visits will not trigger the flag.
		return <FlagComponent flags={flags} onShowFlag={handleShowFlagAutoConversion} />;
	}

	return null;
});
